// Shared interfaces and logic for ChallengeProgressView
export interface ChallengeProgressData {
    challengeId: string;
    userId: string;
    currentValue: number;
    targetValue: number;
    // NEW: Measurement fields for better challenge tracking
    measurementTarget?: number;
    measurementUnit?: string;
    progress: number;
    status: 'in_progress' | 'completed' | 'abandoned';
    startedAt: string;
    lastUpdated: string;
    completedAt?: string;
    pointsEarned: number;
    dailyCompletions?: {
        [date: string]: {
            date: string;
            isCompleted: boolean;
            points: number;
            targetValue: number;
            // NEW: Measurement fields for daily completions
            measurementTarget?: number;
            measurementUnit?: string;
            value: number;
            completedAt?: string | null;
        };
    };
}

export interface ChallengeData {
    id: string;
    title: string;
    description: string;
    level: 'global' | 'organization' | 'team' | 'personal';
    metric: 'steps' | 'distance' | 'calories' | 'minutes' | 'number' | 'hydration' | 'sleep';
    points: number;
    startDate: string;
    endDate: string;
    targetUnit: string;
    targetValue: number;
    // NEW: Measurement fields for better challenge tracking
    measurementTarget?: number;
    measurementUnit?: string;
    type: 'daily' | 'cumulative';
    isActive: boolean;
    organizationId?: string;
    teamId?: string;
    emoji?: string;

    // Media content fields
    videoUrl?: string;
    audioUrl?: string;
    imageUrl?: string;
}

export interface CalendarDay {
    date: string;
    day: number;
    status: 'completed' | 'partial' | 'missed' | 'future';
    value: number;
    target: number;
    isPadding?: boolean;
}

export interface ChallengeProgressViewProps {
    challenge: ChallengeData;
    progressData: ChallengeProgressData;
    onUpdateProgress: (value: number, date?: string) => Promise<void>;
    onDropChallenge: () => Promise<void>;
    onViewLeaderboard: () => void;
}

// Shared utility functions
export const getUnitDisplay = (metric: ChallengeData['metric'], targetUnit?: string) => {
    // If targetUnit is provided, use it as the primary unit
    if (targetUnit) {
        return targetUnit;
    }

    // Fallback to default units for each metric
    switch (metric) {
        case 'steps':
            return 'steps';
        case 'distance':
            return 'km';
        case 'calories':
            return 'calories';
        case 'minutes':
            return 'minutes';
        case 'hydration':
            return 'glasses';
        case 'sleep':
            return 'hours';
        case 'number':
            return 'units';
        default:
            return 'units';
    }
};

export const getMaxValue = (metric: ChallengeData['metric'], targetValue: number) => {
    switch (metric) {
        case 'hydration':
            return Math.max(targetValue, 12); // Max 12 glasses
        case 'sleep':
            return Math.max(targetValue, 12); // Max 12 hours
        case 'minutes':
            return Math.max(targetValue, 1440); // Max 24 hours in minutes
        case 'calories':
            return Math.max(targetValue, 5000); // Max 5000 calories
        case 'steps':
            return Math.max(targetValue, 50000); // Max 50k steps
        case 'distance':
            return Math.max(targetValue, 100); // Max 100km
        default:
            return Math.max(targetValue, 100);
    }
};

export const getStepSize = (metric: ChallengeData['metric']) => {
    switch (metric) {
        case 'hydration':
            return 1;
        case 'sleep':
            return 0.5;
        case 'minutes':
            return 5;
        case 'calories':
            return 10;
        case 'steps':
            return 100;
        case 'distance':
            return 0.1;
        default:
            return 1;
    }
};

export const formatValue = (value: number, metric: ChallengeData['metric'], targetUnit?: string) => {
    switch (metric) {
        case 'minutes':
            if (value >= 60) {
                const hours = Math.floor(value / 60);
                const minutes = value % 60;
                return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}m`;
            }
            return `${value}m`;
        case 'distance':
            return `${value.toFixed(1)}km`;
        case 'sleep':
            return `${value}h`;
        case 'hydration':
            return `${value} ${targetUnit || 'glasses'}`;
        case 'calories':
            return `${value} ${targetUnit || 'cal'}`;
        case 'steps':
            return value >= 1000 ? `${(value / 1000).toFixed(1)}k ${targetUnit || 'steps'}` : `${value} ${targetUnit || 'steps'}`;
        case 'number':
            return `${value} ${targetUnit || 'units'}`;
        default:
            return `${value} ${targetUnit || getUnitDisplay(metric)}`;
    }
};

export const generateCalendarData = (challenge: ChallengeData, progressData: ChallengeProgressData, month?: Date): CalendarDay[] => {
    // Helper functions to get target value and unit from new or legacy fields
    const getTargetValue = () => (challenge as any).measurementTarget || challenge.targetValue;
    const getTargetUnit = () => (challenge as any).measurementUnit || challenge.targetUnit;

    const calendar: CalendarDay[] = [];
    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);

    // If month is provided, use it to determine the calendar range
    let currentDate: Date;
    let displayEndDate: Date;

    if (month) {
        // Start from the first day of the provided month
        currentDate = new Date(month.getFullYear(), month.getMonth(), 1);
        // End at the last day of the provided month
        displayEndDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    } else {
        // Use challenge dates as before
        currentDate = new Date(startDate);
        displayEndDate = endDate;
    }

    // Add padding days at the beginning to align with Sunday
    const startDayOfWeek = currentDate.getDay();
    for (let i = 0; i < startDayOfWeek; i++) {
        const paddingDate = new Date(currentDate);
        paddingDate.setDate(paddingDate.getDate() - (startDayOfWeek - i));
        calendar.push({
            date: paddingDate.toISOString().split('T')[0],
            day: paddingDate.getDate(),
            status: 'future' as const,
            value: 0,
            target: getTargetValue(),
            isPadding: true,
        });
    }

    // Add challenge days
    while (currentDate <= displayEndDate) {
        const dateKey = currentDate.toISOString().split('T')[0];
        const dayData = progressData.dailyCompletions?.[dateKey];

        let status: 'completed' | 'partial' | 'missed' | 'future' = 'future';

        // Only show challenge data for dates within the challenge period
        if (currentDate >= startDate && currentDate <= endDate) {
            // Check if we have data for this day first (regardless of future/past)
            if (dayData) {
                status = dayData.isCompleted ? 'completed' : 'partial';
            } else if (currentDate <= new Date()) {
                // No data and it's today or past - mark as missed
                status = 'missed';
            } else {
                // No data and it's future - mark as future
                status = 'future';
            }
        } else {
            // Outside challenge period
            status = 'future';
        }

        calendar.push({
            date: dateKey,
            day: currentDate.getDate(),
            status,
            value: dayData?.value || 0,
            target: getTargetValue(),
            isPadding: false,
        });

        currentDate.setDate(currentDate.getDate() + 1);
    }

    // Add padding days at the end to complete the grid
    const endDayOfWeek = displayEndDate.getDay();
    const remainingDays = 6 - endDayOfWeek;
    for (let i = 1; i <= remainingDays; i++) {
        const paddingDate = new Date(displayEndDate);
        paddingDate.setDate(paddingDate.getDate() + i);
        calendar.push({
            date: paddingDate.toISOString().split('T')[0],
            day: paddingDate.getDate(),
            status: 'future' as const,
            value: 0,
            target: getTargetValue(),
            isPadding: true,
        });
    }

    return calendar;
};

export const getCurrentMonthDisplay = (challenge: ChallengeData): string => {
    const today = new Date();
    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);

    // If today is within the challenge period, show current month
    if (today >= startDate && today <= endDate) {
        return today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    // If challenge hasn't started, show start month
    if (today < startDate) {
        return startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    // If challenge has ended, show end month
    return endDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const calculateProgressStats = (calendarData: CalendarDay[]) => {
    const completedDays = calendarData.filter(day => day.status === 'completed').length;
    const totalEligibleDays = calendarData.filter(day => day.status !== 'future').length;
    const completionRate = totalEligibleDays > 0 ? (completedDays / totalEligibleDays) * 100 : 0;

    return {
        completedDays,
        totalEligibleDays,
        completionRate,
    };
};

/**
 * Calculate total completed days for the entire challenge period
 * OPTIMIZED: Uses stored participation values when available
 */
export const calculateTotalCompletedDays = (
    challenge: ChallengeData,
    progressData: ChallengeProgressData
): number => {
    // If we have stored pointsEarned, calculate from that (much faster)
    if (progressData.pointsEarned && progressData.pointsEarned > 0) {
        const startDate = new Date(challenge.startDate);
        const endDate = new Date(challenge.endDate);
        const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        const pointsPerDay = Math.round(challenge.points / totalDays);

        if (pointsPerDay > 0) {
            return Math.round(progressData.pointsEarned / pointsPerDay);
        }
    }

    // Fallback to dailyCompletions calculation (only when necessary)
    if (!progressData.dailyCompletions) return 0;

    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);
    const today = new Date();

    let completedDays = 0;
    const completedDates: string[] = [];

    // Count completed days from dailyCompletions
    Object.entries(progressData.dailyCompletions).forEach(([dateKey, dayData]) => {
        const date = new Date(dateKey);

        // Only count days within challenge period
        if (date >= startDate && date <= endDate) {
            if (dayData.isCompleted) {
                completedDays++;
                completedDates.push(dateKey);
            }
        }
    });

    console.log('🔍 calculateTotalCompletedDays (fallback):', {
        challengeId: challenge.id,
        startDate: challenge.startDate,
        endDate: challenge.endDate,
        totalDailyCompletions: Object.keys(progressData.dailyCompletions).length,
        dailyCompletionsKeys: Object.keys(progressData.dailyCompletions),
        completedDays,
        completedDates
    });

    return completedDays;
};

/**
 * Calculate success rate for the entire challenge period
 * OPTIMIZED: Uses stored participation values when available
 */
export const calculateChallengeSuccessRate = (
    challenge: ChallengeData,
    progressData: ChallengeProgressData
): number => {
    // If we have stored progress, use it directly (much faster)
    if (progressData.progress !== undefined && progressData.progress >= 0) {
        return progressData.progress * 100;
    }

    // Fallback to dailyCompletions calculation (only when necessary)
    if (!progressData.dailyCompletions) return 0;

    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);
    const today = new Date();

    // Calculate total challenge days
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Calculate completed days
    let completedDays = 0;
    let eligibleDays = 0;

    Object.entries(progressData.dailyCompletions).forEach(([dateKey, dayData]) => {
        const date = new Date(dateKey);

        // Only count days within challenge period
        if (date >= startDate && date <= endDate) {
            eligibleDays++;
            if (dayData.isCompleted) {
                completedDays++;
            }
        }
    });

    // For days that haven't been recorded yet but are in the past, count as missed
    const currentDate = new Date(startDate);
    const cutoffDate = new Date(Math.min(endDate.getTime(), today.getTime()));
    while (currentDate <= cutoffDate) {
        const dateKey = currentDate.toISOString().split('T')[0];
        if (!progressData.dailyCompletions?.[dateKey]) {
            eligibleDays++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return eligibleDays > 0 ? (completedDays / eligibleDays) * 100 : 0;
};

/**
 * Calculate partial points earned based on progress percentage
 * Awards points proportionally with minimum threshold to avoid rounding to 0
 */
export const calculatePartialPointsEarned = (
    currentValue: number,
    targetValue: number,
    pointsPerDay: number
): number => {
    if (targetValue <= 0) return 0;
    const progressPercentage = Math.min(currentValue / targetValue, 1);

    // Calculate partial points with minimum threshold to avoid rounding to 0
    let pointsEarned = 0;
    if (progressPercentage > 0) {
        // Use Math.ceil to ensure any progress gets at least 1 point
        // But cap it at the daily points
        pointsEarned = Math.min(Math.ceil(progressPercentage * pointsPerDay), pointsPerDay);

        // Ensure minimum of 1 point for any meaningful progress (>= 1% of target)
        if (progressPercentage >= 0.01 && pointsEarned === 0) {
            pointsEarned = 1;
        }
    }

    return pointsEarned;
};

/**
 * Calculate total points earned from daily completions with partial points
 * OPTIMIZED: Uses stored participation values when available
 */
export const calculateTotalPointsEarned = (
    challenge: ChallengeData,
    progressData: ChallengeProgressData
): number => {
    // If we have stored pointsEarned, use it directly (much faster)
    if (progressData.pointsEarned !== undefined && progressData.pointsEarned >= 0) {
        return progressData.pointsEarned;
    }

    // Fallback to dailyCompletions calculation with partial points
    if (!progressData.dailyCompletions) return 0;

    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const pointsPerDay = Math.round(challenge.points / totalDays);

    let totalEarned = 0;
    Object.values(progressData.dailyCompletions).forEach(day => {
        if (day.isCompleted) {
            // Full points for completed days
            totalEarned += pointsPerDay;
        } else if (day.value > 0) {
            // Partial points for days with progress
            const partialPoints = calculatePartialPointsEarned(day.value, day.targetValue, pointsPerDay);
            totalEarned += partialPoints;
        }
    });

    return totalEarned;
};

/**
 * Calculate daily points earned for today's progress with partial points
 * This shows the points that can be earned today, including partial points
 */
export const calculateDailyPointsEarned = (
    challenge: ChallengeData,
    progressData: ChallengeProgressData
): number => {
    if (!progressData.dailyCompletions) return 0;

    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const pointsPerDay = Math.round(challenge.points / totalDays);

    // Get today's date
    const today = new Date().toISOString().split('T')[0];
    const todayCompletion = progressData.dailyCompletions[today];

    // If today is completed, return full points
    if (todayCompletion?.isCompleted) {
        return pointsPerDay;
    }

    // If today has progress, return partial points
    if (todayCompletion?.value && todayCompletion.value > 0) {
        return calculatePartialPointsEarned(todayCompletion.value, todayCompletion.targetValue, pointsPerDay);
    }

    // If today is within challenge period but no progress, return potential points
    const todayDate = new Date();
    if (todayDate >= startDate && todayDate <= endDate) {
        return pointsPerDay;
    }

    return 0;
};

/**
 * Calculate challenge timeline information
 * Returns days elapsed, days remaining, and total challenge duration
 */
export const calculateChallengeTimeline = (
    challenge: ChallengeData
): {
    totalDays: number;
    daysElapsed: number;
    daysRemaining: number;
    startDate: Date;
    endDate: Date;
    currentDate: Date;
} => {
    const startDate = new Date(challenge.startDate);
    const endDate = new Date(challenge.endDate);
    const currentDate = new Date();

    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const daysElapsed = Math.max(0, Math.ceil((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
    const daysRemaining = Math.max(0, totalDays - daysElapsed);

    return {
        totalDays,
        daysElapsed,
        daysRemaining,
        startDate,
        endDate,
        currentDate
    };
};

/**
 * Calculate detailed progress metrics for enhanced display
 * Returns comprehensive progress information including timeline, points, and daily progress
 */
export const calculateDetailedProgressMetrics = (
    challenge: ChallengeData,
    progressData: ChallengeProgressData
): {
    timeline: {
        totalDays: number;
        daysElapsed: number;
        daysRemaining: number;
        progressPercentage: number;
    };
    points: {
        totalEarned: number;
        pointsPerDay: number;
        totalPossible: number;
        completionRate: number;
    };
    todayProgress: {
        currentValue: number;
        targetValue: number;
        progressPercentage: number;
        isCompleted: boolean;
        pointsEarned: number;
    };
    overallProgress: {
        completedDays: number;
        totalEligibleDays: number;
        successRate: number;
    };
} => {
    const timeline = calculateChallengeTimeline(challenge);
    const totalPointsEarned = calculateTotalPointsEarned(challenge, progressData);
    const completedDays = calculateTotalCompletedDays(challenge, progressData);

    // Calculate points per day and total possible points
    const pointsPerDay = challenge.points; // Points per day
    const totalPossiblePoints = challenge.points * timeline.totalDays; // Total points for completing the entire challenge

    // Get today's progress
    const today = new Date().toISOString().split('T')[0];
    const todayCompletion = progressData.dailyCompletions?.[today];
    const targetValue = (challenge as any).measurementTarget || challenge.targetValue;

    // Debug logging to see what's happening
    console.log('🔍 calculateDetailedProgressMetrics DEBUG:', {
        today,
        todayCompletion,
        dailyCompletionsKeys: progressData.dailyCompletions ? Object.keys(progressData.dailyCompletions) : 'null',
        progressDataCurrentValue: progressData.currentValue,
        targetValue
    });

    // Only use today's specific value, not the cumulative currentValue
    const currentValue = todayCompletion?.value || 0;
    const progressPercentage = targetValue > 0 ? Math.min(100, currentValue / targetValue * 100) : 0;
    const isCompleted = todayCompletion?.isCompleted || false;

    // Calculate points earned for today (including partial points)
    let todayPointsEarned = 0;
    if (isCompleted) {
        todayPointsEarned = pointsPerDay;
    } else if (currentValue > 0) {
        todayPointsEarned = calculatePartialPointsEarned(currentValue, targetValue, pointsPerDay);
    }

    const todayProgress = {
        currentValue,
        targetValue,
        progressPercentage,
        isCompleted,
        pointsEarned: todayPointsEarned
    };

    // Calculate overall progress
    const totalEligibleDays = Math.min(timeline.daysElapsed, timeline.totalDays);
    const successRate = totalEligibleDays > 0 ? (completedDays / totalEligibleDays) * 100 : 0;

    return {
        timeline: {
            totalDays: timeline.totalDays,
            daysElapsed: timeline.daysElapsed,
            daysRemaining: timeline.daysRemaining,
            progressPercentage: timeline.totalDays > 0 ? (timeline.daysElapsed / timeline.totalDays) * 100 : 0
        },
        points: {
            totalEarned: totalPointsEarned,
            pointsPerDay: pointsPerDay,
            totalPossible: totalPossiblePoints,
            completionRate: totalPossiblePoints > 0 ? (totalPointsEarned / totalPossiblePoints) * 100 : 0
        },
        todayProgress,
        overallProgress: {
            completedDays,
            totalEligibleDays,
            successRate
        }
    };
};

/**
 * Format progress metrics for display
 * Returns formatted strings for UI display
 */
export const formatProgressMetrics = (
    challenge: ChallengeData,
    progressData: ChallengeProgressData
): {
    timelineText: string;
    pointsText: string;
    overallProgressText: string;
    todayProgressText: string;
} => {
    const metrics = calculateDetailedProgressMetrics(challenge, progressData);
    const unit = (challenge as any).measurementUnit || challenge.targetUnit || getUnitDisplay(challenge.metric);

    // Timeline text
    const timelineText = `Timeline: 0 ---------------------------${metrics.timeline.totalDays} days\n                                  | ${metrics.timeline.daysRemaining} days left`;

    // Points text - show partial points earned
    const completedDays = metrics.overallProgress.completedDays;
    const partialDays = Object.keys(progressData.dailyCompletions || {}).filter(date => {
        const day = progressData.dailyCompletions![date];
        return day.value > 0 && !day.isCompleted;
    }).length;

    let pointsDescription = '';
    if (completedDays > 0 && partialDays > 0) {
        pointsDescription = `(* ${completedDays} days completed, ${partialDays} days with partial progress)`;
    } else if (completedDays > 0) {
        pointsDescription = `(* ${completedDays} days completed)`;
    } else if (partialDays > 0) {
        pointsDescription = `(* ${partialDays} days with partial progress)`;
    } else {
        pointsDescription = `(* each day is ${metrics.points.pointsPerDay} points)`;
    }

    const pointsText = `Total Points Earned: ${metrics.points.totalEarned} ${pointsDescription}`;

    // Overall progress text
    const overallProgressText = `Total progress: ${metrics.points.completionRate.toFixed(1)}%  ${metrics.points.totalEarned}/${metrics.points.totalPossible}. (* each day is ${metrics.points.pointsPerDay} points, ${metrics.timeline.totalDays} days = ${metrics.timeline.totalDays} x ${metrics.points.pointsPerDay}=${metrics.points.totalPossible})`;

    // Today's progress text - show partial points
    const todayValue = formatValue(metrics.todayProgress.currentValue, challenge.metric, unit);
    const todayTarget = formatValue(metrics.todayProgress.targetValue, challenge.metric, unit);
    const todayPointsText = metrics.todayProgress.pointsEarned > 0 ? ` (+${metrics.todayProgress.pointsEarned} points)` : '';
    const todayProgressText = `Today's progress: ${metrics.todayProgress.progressPercentage.toFixed(0)}% (${todayValue} completed out of ${todayTarget})${todayPointsText}`;

    return {
        timelineText,
        pointsText,
        overallProgressText,
        todayProgressText
    };
}; 